import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createInvoice,
  getAllInvoices,
  getBillingProfile,
  getInvoice,
  getInvoicePdfUrl,
  getInvoices,
  getTenantBilling,
  issueInvoice,
  markInvoicePaid,
  previewInvoice,
  saveBillingProfile,
  saveTenantBilling,
  sendInvoice,
  updateInvoice,
  voidInvoice,
} from '../../api/invoices';
import { useEnvironment } from '../useEnvironment/useEnvironment';
import type {
  IBillingProfile,
  IInvoiceInput,
  ISendInvoiceInput,
  ITenantBillingInput,
} from '../../types';

export const useBillingProfile = () => {
  const { environment } = useEnvironment();
  return useQuery({
    queryKey: ['billing-profile', environment],
    queryFn: () => getBillingProfile(environment),
    select: ({ data }) => data,
  });
};

export const useSaveBillingProfile = () => {
  const { environment } = useEnvironment();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (profile: Omit<IBillingProfile, 'updated_at' | 'updated_by'>) =>
      saveBillingProfile(environment, profile),
    onSuccess: () => client.invalidateQueries({ queryKey: ['billing-profile', environment] }),
  });
};

export const useAllInvoices = () => {
  const { environment } = useEnvironment();
  return useQuery({
    queryKey: ['invoices', environment, '_all'],
    queryFn: () => getAllInvoices(environment),
    select: ({ data }) => data,
  });
};

export const useTenantBilling = (tenant: string) => {
  const { environment } = useEnvironment();
  return useQuery({
    queryKey: ['tenant-billing', environment, tenant],
    queryFn: () => getTenantBilling(environment, tenant),
    select: ({ data }) => data,
    enabled: Boolean(tenant),
  });
};

export const useSaveTenantBilling = (tenant: string) => {
  const { environment } = useEnvironment();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: ITenantBillingInput) => saveTenantBilling(environment, tenant, input),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ['tenant-billing', environment, tenant] }),
  });
};

export const useInvoices = (tenant: string) => {
  const { environment } = useEnvironment();
  return useQuery({
    queryKey: ['invoices', environment, tenant],
    queryFn: () => getInvoices(environment, tenant),
    select: ({ data }) => data,
    enabled: Boolean(tenant),
  });
};

export const useInvoice = (tenant: string, id: string) => {
  const { environment } = useEnvironment();
  return useQuery({
    queryKey: ['invoice', environment, tenant, id],
    queryFn: () => getInvoice(environment, tenant, id),
    select: ({ data }) => data,
    enabled: Boolean(tenant && id),
  });
};

export const useInvoicePreview = (
  tenant: string,
  params: { period_start?: string; period_end?: string; issued_on?: string },
  enabled: boolean,
) => {
  const { environment } = useEnvironment();
  return useQuery({
    queryKey: ['invoice-preview', environment, tenant, params],
    queryFn: () => previewInvoice(environment, tenant, params),
    select: ({ data }) => data,
    enabled: Boolean(tenant) && enabled,
    keepPreviousData: true,
  });
};

export const useInvoiceMutations = (tenant: string) => {
  const { environment } = useEnvironment();
  const client = useQueryClient();
  const refresh = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: ['invoices', environment] }),
      client.invalidateQueries({ queryKey: ['invoice', environment, tenant] }),
      client.invalidateQueries({ queryKey: ['invoice-preview', environment, tenant] }),
      client.invalidateQueries({ queryKey: ['tenant-audit', environment, tenant] }),
    ]);
  const create = useMutation({
    mutationFn: (input: IInvoiceInput) => createInvoice(environment, tenant, input),
    onSuccess: refresh,
  });
  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<IInvoiceInput> }) =>
      updateInvoice(environment, tenant, id, input),
    onSuccess: refresh,
  });
  const issue = useMutation({
    mutationFn: (id: string) => issueInvoice(environment, tenant, id),
    onSuccess: refresh,
  });
  const send = useMutation({
    mutationFn: ({ id, input }: { id: string; input: ISendInvoiceInput }) =>
      sendInvoice(environment, tenant, id, input),
    onSuccess: refresh,
  });
  const paid = useMutation({
    mutationFn: ({ id, paidOn }: { id: string; paidOn: string }) =>
      markInvoicePaid(environment, tenant, id, paidOn),
    onSuccess: refresh,
  });
  const voidIt = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string | null }) =>
      voidInvoice(environment, tenant, id, reason),
    onSuccess: refresh,
  });
  const pdf = useMutation({
    mutationFn: ({ id, download }: { id: string; download: boolean }) =>
      getInvoicePdfUrl(environment, tenant, id, download),
  });
  return { create, update, issue, send, paid, void: voidIt, pdf };
};
